import type { Region, SiteCategory } from '../types'

export const INITIAL_VIEW = {
  longitude: -77.55,
  latitude: 40.95,
  zoom: 7.1,
}

// The zoom the marker-declutter algorithm's COLLISION_PX was tuned and visually validated at (see
// useMarkerDeclutter.ts / markerDeclutter.ts), and the zoom at which zoomScaledMarkerSize()
// reproduces today's fixed 26px marker exactly. Note this is INITIAL_VIEW.zoom, not necessarily
// the zoom the app actually opens to — MapContainer's onLoad calls fitToVisibleMarkers(), whose
// fitBounds result varies by viewport and is not currently pinned to this value.
export const REFERENCE_ZOOM = INITIAL_VIEW.zoom

// Covers every real site + the 36 aggregated AT Reuse sites + the 9 individually-pinned AT
// Resource Center sites, with margin. Used with fitBounds
// so the visible area re-frames correctly whichever width the map container currently has
// (e.g. narrower once the List panel opens) instead of holding a fixed center/zoom that
// crops differently depending on the container's aspect ratio.
export const PA_BOUNDS: [[number, number], [number, number]] = [
  [-80.6, 39.55],  // southwest
  [-74.45, 42.25], // northeast
]

// CARTO Voyager GL — locked in 2026-06-02
export const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json'

export const SITE_CATEGORIES: SiteCategory[] = ['OTHER_AT', 'RESEARCH_LAB', 'SMART_LIVING', 'HOUSING', 'AT_RESOURCE_CENTER']

// Plain "AT" on screen — the form Inglis's own material uses, and the one sighted users read
// without stumbling (the dotted "A.T." and hyphenated "A-T" spellings were both tried and both
// read as clutter to people who don't need the accessibility framing). Screen readers pronounce
// a bare "AT" as the English word "at", which turns every one of these labels into a
// preposition-led phrase that means nothing — so the abbreviation is never left for a screen
// reader to interpret: spokenLabel() below expands it to "Assistive Technology" for every
// announced string, and SpokenAT renders the visible-text cases as an aria-hidden "AT" plus an
// sr-only expansion. The lowercase preposition "at" is untouched by both (the match is
// case-sensitive and word-bounded), so ordinary prose still reads normally.
export const SITE_CATEGORY_LABELS: Record<SiteCategory, string> = {
  OTHER_AT: 'Other AT Sites',
  RESEARCH_LAB: 'Research Labs',
  SMART_LIVING: 'Smart Living Sites',
  HOUSING: 'Tech-Integrated Accessible Housing',
  AT_RESOURCE_CENTER: 'AT Resource Centers',
}

export const SITE_CATEGORY_LABELS_SHORT: Record<SiteCategory, string> = {
  OTHER_AT: 'Other AT',
  RESEARCH_LAB: 'Research Lab',
  SMART_LIVING: 'Smart Living',
  HOUSING: 'Housing',
  AT_RESOURCE_CENTER: 'AT Resource Ctr',
}

/**
 * Spoken form of a visible label, for aria-labels and speech-synthesis text only. Expands the
 * "AT" abbreviation to the words it stands for; leaves everything else alone. Never use this for
 * visible text — the abbreviation is what the Inglis material uses on screen (render those with
 * SpokenAT instead).
 *
 * The pattern is deliberately case-sensitive and word-bounded: only the uppercase standalone
 * token is the abbreviation. The English preposition "at" ("at the corner of", "look at") must
 * keep being read as the word it is, and neither "AT" inside a longer token (ATRC, AT&T) nor a
 * plural/possessive ("ATs") is the abbreviation on its own either. The `(?!@)` guard is for one
 * real record in sites.json — the contact address AT@cilncp.org, where \b treats "@" as a
 * boundary and the expansion would otherwise mangle a working email address.
 */
export function spokenLabel(label: string): string {
  return label.replace(/\bAT\b(?!@)/g, 'Assistive Technology')
}

export const REGIONS: Region[] = ['Western PA', 'Central PA', 'Eastern PA']

export const SUGGEST_SITE_EMAIL = 'SDHPinfo@inglis.org'

// AT Reuse Sites — device-reuse/equipment-recycling sites, shown as a single
// aggregated marker + list entry rather than individual pins (see Inglis design review).
// lat/lon is an approximate statewide centroid of all sites, not a real address.
export const REUSE_NETWORK = {
  name: 'AT Reuse Sites',
  lat: 40.4841,
  lon: -76.9100,
  mapUrl: 'https://www.google.com/maps/d/viewer?mid=1ZbXly8xGB_ZtAfhTJiw9tI4AlrCUyrxX',
}
